import { createIcons, LayoutDashboard, Settings, Boxes, Wrench, ShieldAlert, FileText, Briefcase, Activity, ScrollText, Database, Shield, MonitorPlay, ChevronDown, Palette, FileCheck } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { visibleAreas, visibleChildren, type NavigationArea } from "../../permissions/navigation-access.js";
import { resolveAppVersion } from "../../shared/branding/app-version.js";

declare const __APP_VERSION__: string;
declare const __APP_BUILD_TIME__: string;
declare const __APP_BACKEND_ENV__: string;

/**
 * VERSÃO-05: a versão do menu vem sempre da fonte central, nunca do
 * `localStorage`. Ambiente não-produção aparece rotulado ao lado do nome —
 * nunca dentro dele — para não ser confundido com a versão.
 */
function sidebarVersionLabel(): string {
  const version = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ ? __APP_VERSION__ : null;
  const buildNumber = typeof __APP_BUILD_TIME__ !== "undefined" && __APP_BUILD_TIME__ ? __APP_BUILD_TIME__ : null;
  const environment = typeof __APP_BACKEND_ENV__ !== "undefined" && __APP_BACKEND_ENV__ ? __APP_BACKEND_ENV__ : null;
  const appVersion = resolveAppVersion({ version, buildNumber, environment });
  const envBadge = appVersion.environment === "production" ? "" : ` · ${appVersion.environment.toUpperCase()}`;
  return `${appVersion.productName} ${appVersion.displayVersion}${envBadge}`;
}

/**
 * O menu NAO declara mais permissoes proprias: ele le a matriz unica de
 * navegacao (permissions/navigation-access.ts), a mesma que as rotas usam.
 * Foi assim que o RDO voltou a aparecer para Inspetor/Tecnico sem abrir o
 * restante de GESTAO — e e o que garante que Desktop e Drawer mobile nunca
 * divirjam: os dois renderizam a partir deste mesmo calculo.
 */
const AREA_ICONS: Record<string, string> = {
  OVERVIEW: "layout-dashboard",
  EQUIPMENT: "boxes",
  INSPECTIONS: "activity",
  NDT: "monitor-play",
  REPORTS: "file-text",
  INTEGRITY: "shield-alert",
  ENGINEERING: "wrench",
  MANAGEMENT: "briefcase",
  DOCUMENTATION: "scroll-text",
  ADMINISTRATION: "shield",
  SETTINGS: "settings",
  STUDIO: "palette",
  AI_REVIEW: "file-check"
};

class AppSidebar extends HTMLElement {
  private unsubscribe: (() => void) | null = null;
  private menuStates: Map<string, boolean> = new Map();

  connectedCallback() {
    this.render();
    this.unsubscribe = contextStore.subscribe(() => { this.render(); });
    
    this.addEventListener('click', (e) => {
      // Ao navegar de verdade (não o toggle de submenu), fecha o Drawer no mobile.
      const navLink = (e.target as HTMLElement).closest('a[data-link]');
      if (navLink && window.innerWidth < 768) {
        document.querySelector('app-shell')?.removeAttribute('mobile-nav-open');
      }

      const target = (e.target as HTMLElement).closest('.sidebar-item.has-sub');
      if (target) {
        e.preventDefault();
        const path = target.getAttribute('href');
        if (path) {
          const currentPath = window.location.pathname;
          let isCurrentlyExpanded = false;
          if (this.menuStates.has(path)) {
            isCurrentlyExpanded = this.menuStates.get(path)!;
          } else {
            isCurrentlyExpanded = currentPath.startsWith(path + '/');
          }
          
          this.menuStates.set(path, !isCurrentlyExpanded);
          this.render();
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  render() {
    const currentPath = window.location.pathname;
    const has = (permission: string) => contextStore.hasPermission(permission as any);

    const itemsHtml = visibleAreas(has).map((area: NavigationArea) => {
      const children = visibleChildren(has, area);
      const hasSubItems = children.length > 0;
      const icon = AREA_ICONS[area.id] || "activity";

      let isExpanded = false;
      if (this.menuStates.has(area.path)) {
        isExpanded = this.menuStates.get(area.path)!;
      } else {
        isExpanded = currentPath.startsWith(area.path + '/');
      }

      let html = `
        <a href="${area.path}" class="sidebar-item ${currentPath === area.path ? 'active' : ''} ${hasSubItems ? 'has-sub' : ''}" ${!hasSubItems ? 'data-link' : ''}>
          <i><i data-lucide="${icon}"></i></i>
          <span>${area.label}</span>
          ${hasSubItems ? `<i><i data-lucide="chevron-down" style="transform: rotate(${isExpanded ? '180deg' : '0deg'}); transition: transform 0.2s;"></i></i>` : ''}
        </a>
      `;

      if (hasSubItems && isExpanded) {
        html += `<div class="sidebar-submenus">`;
        children.forEach(sub => {
          html += `
            <a href="${sub.path}" class="sidebar-subitem ${currentPath === sub.path ? 'active' : ''}" data-link>
              <span>${sub.label}</span>
            </a>
          `;
        });
        html += `</div>`;
      }

      return html;
    }).join("");

    this.innerHTML = `
      <nav class="sidebar-nav">
        ${itemsHtml}
      </nav>
      <div class="sidebar-footer">
        <span class="sidebar-version">${sidebarVersionLabel()}</span>
      </div>
    `;

    createIcons({
      icons: { LayoutDashboard, Settings, Boxes, Wrench, ShieldAlert, FileText, Briefcase, Activity, ScrollText, Database, Shield, MonitorPlay, ChevronDown, Palette, FileCheck },
      nameAttr: "data-lucide",
      root: this
    });
  }
}

if (!customElements.get("app-sidebar")) {
  customElements.define("app-sidebar", AppSidebar);
}
